import { loadRuntimeConfig } from './config';
import { createWebPreview } from './imagePreview';

export const AUTH_EXPIRED_EVENT = 'human-studio:auth-expired';

export interface Photographer {
  photographerId: string;
  email: string;
  displayName: string;
  slug: string;
  notificationPreferences: NotificationPreferences;
  showOnboardingGuide: boolean;
}

export interface NotificationPreferences {
  favouriteSelections: boolean;
  galleryDownloads: boolean;
}

export interface UpdateProfileInput {
  displayName: string;
  slug: string;
  notificationPreferences: NotificationPreferences;
}

export type SubscriptionPlanId = 'free' | 'essential' | 'studio' | 'enterprise' | 'trial';
export type BillingInterval = 'none' | 'monthly' | 'annual' | 'custom';

export interface SubscriptionLimits {
  storageBytes?: number;
  activeGalleries?: number;
  photosPerGallery: number;
}

export interface SubscriptionPlan {
  planId: SubscriptionPlanId;
  name: string;
  description: string;
  public: boolean;
  sortOrder: number;
  monthlyPriceCents?: number;
  annualPriceCents?: number;
  pricingMode: 'free' | 'fixed' | 'quote' | 'internal';
  limits: SubscriptionLimits;
  features?: { bookings?: boolean };
  minimumStorageBytes?: number;
}

export interface Subscription {
  photographerId: string;
  email: string;
  planId: SubscriptionPlanId;
  billingInterval: BillingInterval;
  customPriceCents?: number;
  customLimits?: Partial<SubscriptionLimits>;
  storageBytesUsed: number;
  activeGalleriesUsed: number;
  pendingChangeRequest?: {
    requestId: string;
    targetPlanId: Exclude<SubscriptionPlanId, 'trial'>;
    billingInterval: BillingInterval;
    note?: string;
    requestedAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionView {
  subscription: Subscription;
  plan: SubscriptionPlan;
  effectiveLimits: SubscriptionLimits;
}

export type BookingStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'CHANGES_SUGGESTED' | 'EXPIRED' | 'CANCELLED';
export type BookingListCategory = 'pending' | 'upcoming' | 'past' | 'closed';
export type Weekday = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';
export interface AvailabilityWindow { start: string; end: string }
export type WeeklyAvailability = Record<Weekday, AvailabilityWindow[]>;
export interface BookingShootType {
  shootTypeId: string;
  name: string;
  durationMinutes: number;
  priceCents?: number;
}
export interface BookingPaymentPolicy {
  enabled: boolean;
  depositPercent: number;
  bankName?: string;
  accountHolder?: string;
  accountNumber?: string;
  branchCode?: string;
}
export interface BookingPaymentSummary {
  requiredBeforeAcceptance: true;
  depositPercent: number;
  quotationNumber: string;
  sessionTotalCents: number;
  depositDueCents: number;
  remainingBalanceCents: number;
}
export interface BookingSettings {
  photographerId: string;
  enabled: boolean;
  shootTypes: BookingShootType[];
  /** Deprecated compatibility fields derived from the first shoot type. */
  sessionDurationMinutes: number;
  priceCents?: number;
  slotCadenceMinutes: 15 | 30 | 60;
  bufferMinutes: number;
  minimumNoticeHours: number;
  bookingHorizonDays: number;
  intro?: string;
  colorScheme: import('./galleryCustomisation').GalleryColorScheme;
  weeklyAvailability: WeeklyAvailability;
  paymentPolicy: BookingPaymentPolicy;
  coverStorageKey?: string;
  coverFocalPoint?: CoverFocalPoint;
  coverUrl?: string;
  createdAt: string;
  updatedAt: string;
}
export interface BookingException { photographerId: string; date: string; windows: AvailabilityWindow[]; createdAt: string; updatedAt: string }
export interface BookingClient { fullName: string; email: string; phone: string; shootLocation: string; details: string }
export interface Booking {
  bookingId: string;
  ownerId?: string;
  photographerSlug?: string;
  photographerName: string;
  photographerEmail?: string;
  status: BookingStatus;
  scheduledStart: string;
  scheduledEnd: string;
  shootTypeId?: string;
  shootTypeName?: string;
  durationMinutes: number;
  bufferMinutes?: number;
  priceCents?: number;
  payment?: BookingPaymentSummary;
  paymentConfirmedAt?: string;
  client: BookingClient;
  holdExpiresAt?: string;
  decisionNote?: string;
  createdAt?: string;
  updatedAt?: string;
}
export interface BookingSlot { start: string; end: string; localDate: string; localTime: string }
export interface BookingSettingsResponse {
  settings: BookingSettings;
  entitlement: { bookings: boolean };
  publicUrl: string;
}
export interface PublicBookingPage {
  photographer: { name: string; slug: string };
  settings: Pick<BookingSettings, 'shootTypes' | 'sessionDurationMinutes' | 'priceCents' | 'intro' | 'colorScheme' | 'coverFocalPoint' | 'coverUrl'> & {
    payment: { requiredBeforeAcceptance: boolean; depositPercent: number; holdHours: number };
  };
  available: boolean;
}

export type CoverFont =
  | 'montserrat'
  | 'raleway'
  | 'lora'
  | 'playfair-display'
  | 'cormorant-garamond'
  | 'cinzel'
  | 'caveat'
  | 'dancing-script';

export interface CoverFocalPoint {
  x: number;
  y: number;
}

export interface Gallery {
  galleryId: string;
  ownerId: string;
  photographerSlug: string;
  gallerySlug: string;
  title: string;
  description?: string;
  coverFont?: CoverFont;
  customisation?: GalleryCustomisation;
  eventDate?: string;
  coverPhotoId?: string;
  coverFocalPoint?: CoverFocalPoint;
  coverUrl?: string;
  coverMediaType?: 'image' | 'video';
  coverPlaybackUrl?: string;
  scenes?: string[];
  isArchived: boolean;
  photoCount: number;
  imageCount?: number;
  videoCount?: number;
  favourites?: GalleryFavouritesConfig | null;
  watermarkConfig?: GalleryWatermarkConfig;
  activeWatermarkConfig?: GalleryWatermarkConfig;
  activeWatermarkVersion?: string;
  pendingWatermarkVersion?: string;
  watermarkStatus?: 'PROCESSING' | 'ACTIVE' | 'FAILED';
  watermarkReadyCount?: number;
  watermarkTargetCount?: number;
  watermarkFailureMessage?: string;
  watermarkQuota?: WatermarkQuota;
  createdAt: string;
  updatedAt: string;
}

export type WatermarkPosition =
  | 'top-left' | 'top-center' | 'top-right'
  | 'center-left' | 'center' | 'center-right'
  | 'bottom-left' | 'bottom-center' | 'bottom-right';

export interface GalleryWatermarkConfig {
  watermarkId: string;
  position: WatermarkPosition;
  sizePercent: number;
  opacityPercent: number;
  marginPercent: 3;
}

export interface WatermarkAsset {
  ownerId: string;
  watermarkId: string;
  name: string;
  url: string;
  sizeBytes: number;
  width: number;
  height: number;
  createdAt: string;
  updatedAt: string;
}

export interface WatermarkQuota {
  limit: number;
  remaining: number;
  resetAt: string;
}

export interface PublicGallery {
  galleryId: string;
  photographerSlug: string;
  gallerySlug: string;
  title: string;
  description?: string;
  coverFont?: CoverFont;
  customisation?: GalleryCustomisation;
  eventDate?: string;
  coverFocalPoint?: CoverFocalPoint;
  coverUrl?: string;
  coverMediaType?: 'image' | 'video';
  coverPlaybackUrl?: string;
  scenes?: string[];
  photoCount: number;
  imageCount?: number;
  videoCount?: number;
  favourites?: GalleryFavouritesConfig | null;
  createdAt: string;
}

export interface GalleryFavouritesConfig {
  limit: number;
}

export interface GalleryCustomisation {
  colorScheme: import('./galleryCustomisation').GalleryColorScheme;
  galleryStyle: import('./galleryCustomisation').GalleryStyle;
  backgroundColor: import('./galleryCustomisation').GalleryBackgroundColor;
  accentColor: import('./galleryCustomisation').GalleryAccentColor;
  accentTextColor: import('./galleryCustomisation').GalleryAccentTextColor;
  [key: string]: unknown;
}

export type FavouriteSelectionStatus = 'SUBMITTED' | 'REOPENED';

export interface PublicFavouriteSelection {
  photoIds: string[];
  status: FavouriteSelectionStatus;
  revision: number;
  submittedAt: string;
  reopenedAt?: string;
  updatedAt: string;
}

export interface FavouriteSelection extends PublicFavouriteSelection {
  clientKey: string;
  clientName: string;
  clientEmail: string;
  count: number;
}

export interface Photo {
  photoId: string;
  galleryId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  previewContentType?: string;
  previewSizeBytes?: number;
  width?: number;
  height?: number;
  previewWidth?: number;
  previewHeight?: number;
  scene?: string;
  createdAt: string;
  url?: string;
  mediaType?: 'image' | 'video';
}

export interface Video extends Photo {
  mediaType: 'video';
  durationSeconds: number;
  videoCodec: string;
  audioCodec?: string;
  posterTimestampSeconds: number;
  posterUrl: string;
  playbackUrl: string;
}

export interface PhotoPageOptions {
  limit?: number;
  cursor?: string;
  scene?: string;
}

export const PUBLIC_UNASSIGNED_SCENE = '__unassigned';

export interface GalleryPhotoResponse<TGallery extends Gallery | PublicGallery = Gallery> {
  gallery: TGallery;
  photos: Photo[];
  videos?: Video[];
  nextCursor?: string;
}

export type UploadStage =
  | 'preparing'
  | 'requesting'
  | 'uploading-original'
  | 'uploading-preview'
  | 'uploading-video'
  | 'uploading-poster'
  | 'completing-video'
  | 'complete';

export interface UploadProgress {
  fileName: string;
  stage: UploadStage;
  percent: number;
}

export interface PhotoDuplicate {
  index: number;
  fileName: string;
  source: 'gallery' | 'batch';
}

export interface ExportPart {
  partIndex: number;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';
  photoCount: number;
  sizeBytes: number;
  url?: string;
  errorMessage?: string;
  sceneName?: string;
}

export type ExportQuality = 'web' | 'original';

export interface ExportJob {
  exportId: string;
  galleryId: string;
  quality: ExportQuality;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';
  parts: ExportPart[];
  createdAt: string;
  updatedAt: string;
}

export interface GalleryStatisticsJob {
  quality: ExportQuality;
  status: ExportJob['status'];
  photoCount: number;
  sizeBytes: number;
  createdAt: string;
}

export interface GalleryStatisticsResponse {
  retentionDays: number;
  readyCount: number;
  pendingCount: number;
  failedCount: number;
  webCount: number;
  originalCount: number;
  latestReadyAt?: string;
  recent: GalleryStatisticsJob[];
}

export interface CreateGalleryInput {
  title: string;
  slug?: string;
  description?: string;
  eventDate?: string | null;
  passcode: string;
}

export interface UpdateGalleryInput {
  title?: string;
  slug?: string;
  description?: string | null;
  coverFont?: CoverFont;
  customisation?: Partial<Pick<GalleryCustomisation, 'colorScheme' | 'galleryStyle'>>;
  eventDate?: string | null;
  passcode?: string;
  coverPhotoId?: string | null;
  coverFocalPoint?: CoverFocalPoint | null;
  isArchived?: boolean;
  scenes?: string[];
  favourites?: GalleryFavouritesConfig | null;
  watermark?: GalleryWatermarkConfig | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details: Record<string, unknown>
  ) {
    super(message);
  }
}

export async function apiRequest<T>(
  path: string,
  options: {
    method?: string;
    token?: string | null;
    publicSession?: string | null;
    favouriteSession?: string | null;
    body?: unknown;
    signal?: AbortSignal;
  } = {}
): Promise<T> {
  const config = await loadRuntimeConfig();
  const headers = new Headers();
  if (options.body !== undefined) {
    headers.set('content-type', 'application/json');
  }
  if (options.token) {
    headers.set('authorization', `Bearer ${options.token}`);
  }
  if (options.publicSession) {
    headers.set('x-human-studio-session', options.publicSession);
  }
  if (options.favouriteSession) {
    headers.set('x-human-studio-favourite', options.favouriteSession);
  }

  const response = await fetch(`${config.apiBaseUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    signal: options.signal,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({ message: 'Request failed' }));
    if (options.token && (response.status === 401 || response.status === 403)) {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    throw new ApiError(payload.message ?? 'Request failed', response.status, payload);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export function listGalleries(token: string) {
  return apiRequest<{ photographer: Photographer; galleries: Gallery[] }>('/galleries', { token });
}

export function listPublicSubscriptionPlans() {
  return apiRequest<{ plans: SubscriptionPlan[] }>('/public/subscription-plans');
}

export function getSubscription(token: string) {
  return apiRequest<SubscriptionView>('/subscription', { token });
}

export function requestSubscriptionChange(
  token: string,
  input: { targetPlanId: Exclude<SubscriptionPlanId, 'trial'>; billingInterval: BillingInterval; note?: string }
) {
  return apiRequest<SubscriptionView>('/subscription/change-requests', { method: 'POST', token, body: input });
}

export function updateProfile(token: string, input: UpdateProfileInput) {
  return apiRequest<{ photographer: Photographer }>('/profile', {
    method: 'PATCH',
    token,
    body: input
  });
}

export function completeSignupProfile(token: string, displayName?: string) {
  return apiRequest<{ photographer: Photographer }>('/profile/signup', {
    method: 'POST',
    token,
    body: displayName === undefined ? {} : { displayName }
  });
}

export function completeOnboarding(token: string) {
  return apiRequest<{ photographer: Photographer }>('/profile/onboarding/complete', {
    method: 'POST',
    token
  });
}

export async function getBookingSettings(token: string) {
  return normalizeBookingSettingsResponse(await apiRequest<BookingSettingsResponse>('/bookings/settings', { token }));
}

export async function updateBookingSettings(token: string, input: Partial<BookingSettings>) {
  const rawResponse = await apiRequest<BookingSettingsResponse>('/bookings/settings', { method: 'PATCH', token, body: input });
  if (input.shootTypes && !rawResponse.settings.shootTypes?.length) {
    throw new Error('Shoot types could not be saved. Refresh the page and try again.');
  }
  return normalizeBookingSettingsResponse(rawResponse);
}

export async function uploadBookingCover(token: string, file: File, focalPoint: CoverFocalPoint = { x: 50, y: 50 }) {
  const preview = await createWebPreview(file);
  if (preview.sizeBytes > 5 * 1024 * 1024) throw new Error('Booking cover preview must be 5 MB or smaller');
  const upload = await apiRequest<{ coverId: string; storageKey: string; uploadUrl: string }>('/bookings/settings/cover/uploads', {
    method: 'POST', token, body: { contentType: preview.contentType, sizeBytes: preview.sizeBytes }
  });
  await uploadBlob(upload.uploadUrl, preview.blob, preview.contentType, () => undefined);
  return normalizeBookingSettingsResponse(await apiRequest<BookingSettingsResponse>(`/bookings/settings/cover/uploads/${upload.coverId}/complete`, {
    method: 'POST', token, body: { storageKey: upload.storageKey, focalPoint }
  }));
}

export async function deleteBookingCover(token: string) {
  return normalizeBookingSettingsResponse(await apiRequest<BookingSettingsResponse>('/bookings/settings/cover', { method: 'DELETE', token }));
}

function normalizeBookingSettingsResponse(response: BookingSettingsResponse): BookingSettingsResponse {
  return {
    ...response,
    settings: {
      ...response.settings,
      shootTypes: response.settings.shootTypes?.length ? response.settings.shootTypes : [{
        shootTypeId: 'default',
        name: 'Photography session',
        durationMinutes: response.settings.sessionDurationMinutes,
        priceCents: response.settings.priceCents
      }],
      paymentPolicy: response.settings.paymentPolicy ?? { enabled: false, depositPercent: 50 }
    }
  };
}

export function listBookingExceptions(token: string, from?: string, to?: string) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return apiRequest<{ exceptions: BookingException[] }>(`/bookings/exceptions${params.size ? `?${params}` : ''}`, { token });
}

export function putBookingException(token: string, date: string, windows: AvailabilityWindow[]) {
  return apiRequest<{ exception: BookingException }>(`/bookings/exceptions/${date}`, { method: 'PUT', token, body: { windows } });
}

export function deleteBookingException(token: string, date: string) {
  return apiRequest<void>(`/bookings/exceptions/${date}`, { method: 'DELETE', token });
}

export function listBookings(token: string, category: BookingListCategory, cursor?: string) {
  const params = new URLSearchParams({ category });
  if (cursor) params.set('cursor', cursor);
  return apiRequest<{ bookings: Booking[]; nextCursor?: string; entitlement: { bookings: boolean }; category: BookingListCategory }>(`/bookings?${params}`, { token });
}

export function listBookingCalendar(token: string, month: string) {
  return apiRequest<{ bookings: Booking[] }>(`/bookings/calendar?month=${encodeURIComponent(month)}`, { token });
}

export function getBooking(token: string, bookingId: string) {
  return apiRequest<{ booking: Booking }>(`/bookings/${bookingId}`, { token });
}

export function decideBooking(token: string, bookingId: string, action: 'accept' | 'reject' | 'suggest-changes' | 'cancel', note?: string, paymentConfirmed?: boolean) {
  return apiRequest<{ booking: Booking }>(`/bookings/${bookingId}/${action}`, { method: 'POST', token, body: { note, paymentConfirmed } });
}

export function deleteBookingRecord(token: string, bookingId: string) {
  return apiRequest<void>(`/bookings/${bookingId}`, { method: 'DELETE', token });
}

export function getPublicBookingPage(slug: string) {
  return apiRequest<PublicBookingPage>(`/public/bookings/${encodeURIComponent(slug)}`);
}

export function getPublicBookingAvailability(slug: string, month: string, shootTypeId?: string) {
  const params = new URLSearchParams({ month });
  if (shootTypeId) params.set('shootTypeId', shootTypeId);
  return apiRequest<{ month: string; shootTypeId?: string; slots: BookingSlot[] }>(`/public/bookings/${encodeURIComponent(slug)}/availability?${params}`);
}

export function submitBookingRequest(slug: string, input: {
  idempotencyKey: string;
  shootTypeId?: string;
  scheduledStart: string;
  fullName: string;
  email: string;
  phone: string;
  shootLocation: string;
  details: string;
  website?: string;
}) {
  return apiRequest<{ booking: Booking; statusUrl: string; replayed: boolean }>(`/public/bookings/${encodeURIComponent(slug)}/requests`, { method: 'POST', body: input });
}

export function getBookingStatus(token: string) {
  return apiRequest<{ booking: Booking }>(`/public/bookings/status/${encodeURIComponent(token)}`);
}

export function getBookingAction(token: string) {
  return apiRequest<{ booking: Booking; action: 'ACCEPT' | 'REJECT'; expired: boolean }>(`/public/bookings/actions/${encodeURIComponent(token)}`);
}

export function confirmBookingAction(token: string, note?: string, paymentConfirmed?: boolean) {
  return apiRequest<{ booking: Booking; confirmed: true }>(`/public/bookings/actions/${encodeURIComponent(token)}/confirm`, { method: 'POST', body: { note, paymentConfirmed } });
}

export function createGallery(token: string, input: CreateGalleryInput) {
  return apiRequest<{ gallery: Gallery; shareUrl: string }>('/galleries', {
    method: 'POST',
    token,
    body: input
  });
}

export function getGallery(token: string, galleryId: string, options: PhotoPageOptions = {}) {
  return apiRequest<GalleryPhotoResponse>(withPhotoPage(`/galleries/${galleryId}`, options), { token });
}

export function getGalleryStatistics(token: string, galleryId: string) {
  return apiRequest<GalleryStatisticsResponse>(`/galleries/${galleryId}/statistics`, { token });
}

export function updateGallery(token: string, galleryId: string, input: UpdateGalleryInput) {
  return apiRequest<{ gallery: Gallery }>(`/galleries/${galleryId}`, {
    method: 'PATCH',
    token,
    body: input
  });
}

export function shareGalleryByEmail(token: string, galleryId: string, input: { recipientEmail: string; passcode: string; customMessage?: string }) {
  return apiRequest<{ sent: true; recipientEmail: string }>(`/galleries/${galleryId}/share-email`, {
    method: 'POST',
    token,
    body: input
  });
}

export function listWatermarks(token: string) {
  return apiRequest<{ watermarks: WatermarkAsset[] }>('/watermarks', { token });
}

export async function uploadWatermark(token: string, name: string, file: File) {
  const upload = await apiRequest<{ watermarkId: string; uploadUrl: string }>('/watermarks/uploads', {
    method: 'POST',
    token,
    body: { name, fileName: file.name, contentType: file.type || 'image/png', sizeBytes: file.size }
  });
  await uploadBlob(upload.uploadUrl, file, 'image/png', () => undefined);
  return apiRequest<{ watermark: WatermarkAsset }>(`/watermarks/uploads/${upload.watermarkId}/complete`, {
    method: 'POST',
    token,
    body: { name }
  });
}

export function renameWatermark(token: string, watermarkId: string, name: string) {
  return apiRequest<{ watermark: WatermarkAsset }>(`/watermarks/${watermarkId}`, { method: 'PATCH', token, body: { name } });
}

export function deleteWatermark(token: string, watermarkId: string) {
  return apiRequest<void>(`/watermarks/${watermarkId}`, { method: 'DELETE', token });
}

export function retryGalleryWatermark(token: string, galleryId: string) {
  return apiRequest<{ gallery: Gallery }>(`/galleries/${galleryId}/watermark/retry`, { method: 'POST', token });
}

export function archiveGallery(token: string, galleryId: string) {
  return updateGallery(token, galleryId, { isArchived: true });
}

export function deleteGallery(token: string, galleryId: string) {
  return apiRequest<void>(`/galleries/${galleryId}`, { method: 'DELETE', token });
}

export function updatePhotoScenes(token: string, galleryId: string, input: { photoIds: string[]; scene: string | null }) {
  return apiRequest<{ photoIds: string[]; scene: string | null }>(`/galleries/${galleryId}/photos/scenes`, {
    method: 'PATCH',
    token,
    body: input
  });
}

export function listFavouriteSelections(token: string, galleryId: string) {
  return apiRequest<{ selections: FavouriteSelection[] }>(`/galleries/${galleryId}/favourite-selections`, { token });
}

export function reopenFavouriteSelection(token: string, galleryId: string, clientKey: string) {
  return apiRequest<{ selection: FavouriteSelection }>(
    `/galleries/${galleryId}/favourite-selections/${clientKey}/reopen`,
    { method: 'POST', token }
  );
}

export function createFavouriteSelectionExport(token: string, galleryId: string, clientKey: string) {
  return apiRequest<{ exportJob: ExportJob }>(
    `/galleries/${galleryId}/favourite-selections/${clientKey}/export`,
    { method: 'POST', token }
  );
}

export function deletePhoto(token: string, galleryId: string, photoId: string) {
  return apiRequest<void>(`/galleries/${galleryId}/photos/${photoId}`, { method: 'DELETE', token });
}

export function checkPhotoDuplicates(token: string, galleryId: string, fileNames: string[], signal?: AbortSignal) {
  return apiRequest<{ duplicates: PhotoDuplicate[] }>(`/galleries/${galleryId}/photos/duplicates`, {
    method: 'POST',
    token,
    signal,
    body: { fileNames }
  });
}

export function bulkDeletePhotos(token: string, galleryId: string, photoIds: string[]) {
  return apiRequest<{ deletedPhotoIds: string[]; alreadyMissingPhotoIds: string[] }>(`/galleries/${galleryId}/photos`, {
    method: 'DELETE',
    token,
    body: { photoIds }
  });
}

export function getVideoDownloadUrl(token: string, galleryId: string, videoId: string) {
  return apiRequest<{ url: string; expiresInSeconds: number }>(`/galleries/${galleryId}/videos/${videoId}/download-url`, {
    method: 'POST',
    token
  });
}

export async function uploadPhoto(
  token: string,
  galleryId: string,
  file: File,
  options: { onProgress?: (progress: UploadProgress) => void; signal?: AbortSignal } = {}
) {
  const report = (stage: UploadStage, percent: number) => {
    options.onProgress?.({ fileName: file.name, stage, percent: Math.min(100, Math.max(0, Math.round(percent))) });
  };

  report('preparing', 1);
  const preview = await createWebPreview(file, options.signal);
  throwIfUploadAborted(options.signal);
  report('requesting', 8);

  let upload: {
    photoId: string;
    storageKey: string;
    uploadUrl: string;
    previewStorageKey?: string;
    previewUploadUrl?: string;
  } | undefined;
  try {
    upload = await apiRequest<{
      photoId: string;
      storageKey: string;
      uploadUrl: string;
      previewStorageKey?: string;
      previewUploadUrl?: string;
    }>(
      `/galleries/${galleryId}/photos/upload-url`, {
      method: 'POST',
      token,
      signal: options.signal,
      body: {
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        previewContentType: preview.contentType,
        previewSizeBytes: preview.sizeBytes
      }
    });

    await uploadBlob(upload.uploadUrl, file, file.type, (percent) => report('uploading-original', 10 + percent * 0.65), options.signal);
    if (upload.previewUploadUrl && upload.previewStorageKey) {
      await uploadBlob(upload.previewUploadUrl, preview.blob, preview.contentType, (percent) => report('uploading-preview', 75 + percent * 0.2), options.signal);
    }
    throwIfUploadAborted(options.signal);
    report('complete', 96);

    const completeInput = {
      method: 'POST',
      token,
      signal: options.signal,
      body: {
        photoId: upload.photoId,
        storageKey: upload.storageKey,
        previewStorageKey: upload.previewStorageKey,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        previewContentType: preview.contentType,
        previewSizeBytes: preview.sizeBytes,
        width: preview.originalWidth,
        height: preview.originalHeight,
        previewWidth: preview.width,
        previewHeight: preview.height
      }
    } as const;
    const result = await completePhotoUploadWithRetry(
      () => apiRequest<{ photo: Photo }>(`/galleries/${galleryId}/photos/complete`, completeInput),
      options.signal
    );
    report('complete', 100);
    return result;
  } catch (error) {
    if (upload) {
      await apiRequest<void>(`/galleries/${galleryId}/photos/uploads/${upload.photoId}/abort`, {
        method: 'POST',
        token
      }).catch(() => undefined);
    }
    throw error;
  }
}

async function completePhotoUploadWithRetry<T>(
  complete: () => Promise<T>,
  signal?: AbortSignal
): Promise<T> {
  try {
    return await complete();
  } catch (error) {
    if (signal?.aborted || !isRetryablePhotoCompletionError(error)) {
      throw error;
    }
    return complete();
  }
}

function isRetryablePhotoCompletionError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 408 || error.status === 429 || error.status >= 500;
  }
  return error instanceof TypeError || (error instanceof Error && error.name === 'TypeError');
}

function uploadBlob(
  url: string,
  body: Blob,
  contentType: string,
  onProgress: (percent: number) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    request.open('PUT', url);
    request.setRequestHeader('content-type', contentType);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress((event.loaded / event.total) * 100);
      }
    };
    request.onload = () => {
      signal?.removeEventListener('abort', abort);
      if (request.status >= 200 && request.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      reject(new Error(`Upload failed with status ${request.status}`));
    };
    request.onerror = () => reject(new Error('Upload failed because the network request could not be completed'));
    request.onabort = () => reject(new DOMException('Upload was cancelled.', 'AbortError'));
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) {
      request.abort();
      return;
    }
    request.send(body);
  });
}

function throwIfUploadAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Upload was cancelled.', 'AbortError');
  }
}

export function createExport(token: string, galleryId: string, quality: ExportQuality) {
  return apiRequest<{ exportJob: ExportJob }>(`/galleries/${galleryId}/exports`, { method: 'POST', token, body: { quality } });
}

export function getExport(token: string, exportId: string) {
  return apiRequest<ExportJob>(`/exports/${exportId}`, { token });
}

export function getPublicGallery(photographerSlug: string, gallerySlug: string) {
  return apiRequest<{ gallery: PublicGallery; locked: boolean }>(`/public/galleries/${photographerSlug}/${gallerySlug}`);
}

export function unlockPublicGallery(photographerSlug: string, gallerySlug: string, passcode: string) {
  return apiRequest<{ token: string; gallery: PublicGallery }>(`/public/galleries/${photographerSlug}/${gallerySlug}/session`, {
    method: 'POST',
    body: { passcode }
  });
}

export function getPublicPhotos(photographerSlug: string, gallerySlug: string, publicSession: string, options: PhotoPageOptions = {}) {
  return apiRequest<GalleryPhotoResponse<PublicGallery>>(
    withPhotoPage(`/public/galleries/${photographerSlug}/${gallerySlug}/photos`, options),
    {
      publicSession
    }
  );
}

export function createPublicFavouriteSession(
  photographerSlug: string,
  gallerySlug: string,
  publicSession: string,
  input: { name: string; email: string }
) {
  return apiRequest<{ token: string; client: { clientKey: string; name: string; email: string } }>(
    `/public/galleries/${photographerSlug}/${gallerySlug}/favourites/session`,
    { method: 'POST', publicSession, body: input }
  );
}

export function getPublicFavouriteSelection(
  photographerSlug: string,
  gallerySlug: string,
  publicSession: string,
  favouriteSession: string
) {
  return apiRequest<{ selection: PublicFavouriteSelection | null }>(
    `/public/galleries/${photographerSlug}/${gallerySlug}/favourites`,
    { publicSession, favouriteSession }
  );
}

export function submitPublicFavouriteSelection(
  photographerSlug: string,
  gallerySlug: string,
  publicSession: string,
  favouriteSession: string,
  input: { photoIds: string[]; submissionId: string }
) {
  return apiRequest<{ selection: PublicFavouriteSelection }>(
    `/public/galleries/${photographerSlug}/${gallerySlug}/favourites/submit`,
    { method: 'POST', publicSession, favouriteSession, body: input }
  );
}

export function createPublicExport(photographerSlug: string, gallerySlug: string, publicSession: string, quality: ExportQuality) {
  return apiRequest<{ exportJob: ExportJob }>(`/public/galleries/${photographerSlug}/${gallerySlug}/exports`, {
    method: 'POST',
    publicSession,
    body: { quality }
  });
}

export function getPublicExport(exportId: string, publicSession: string) {
  return apiRequest<ExportJob>(`/public/exports/${exportId}`, { publicSession });
}

export function getPublicVideoDownloadUrl(
  photographerSlug: string,
  gallerySlug: string,
  videoId: string,
  publicSession: string
) {
  return apiRequest<{ url: string; expiresInSeconds: number }>(
    `/public/galleries/${photographerSlug}/${gallerySlug}/videos/${videoId}/download-url`,
    { method: 'POST', publicSession }
  );
}

function withPhotoPage(path: string, options: PhotoPageOptions): string {
  const params = new URLSearchParams();
  if (options.limit) {
    params.set('limit', String(options.limit));
  }
  if (options.cursor) {
    params.set('cursor', options.cursor);
  }
  if (options.scene) {
    params.set('scene', options.scene);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
